/**
 * 动态奖励运维 — 对碰 + 平衡奖触发
 */
import { useState } from 'react';
import {
  Button,
  Input,
  Typography,
  message,
  Card,
  Descriptions,
  Form,
  Space,
  Statistic,
  Row,
  Col,
  Table,
  Alert,
} from 'antd';
import { api, ApiError } from '../../api/client';

interface PairingState {
  user_id: number;
  leg_l: string;
  leg_r: string;
}

interface PairingCalc {
  pairing_amount: string;
  leg_l_after: string;
  leg_r_after: string;
}

interface PreviewResult {
  state: PairingState;
  pairing: PairingCalc;
  gross_balance_award: string;
  balance_award_pct: string;
}

interface ExecutePairing extends PairingCalc {
  user_id: number;
  left_new: string;
  right_new: string;
  leg_l_before: string;
  leg_r_before: string;
}

interface BalanceAwardResult {
  requested?: string;
  awarded: string;
  capped: boolean;
  remaining_after?: string;
  skipped?: string;
}

interface MentorRow {
  mentor_id: number;
  mentor_level: string;
  circle: number;
  pct: string;
  base_amount: string;
  bonus_amount: string;
  source_user_id: number;
}

interface ExecuteResult {
  pairing: ExecutePairing;
  balance_award: BalanceAwardResult;
  mentorship: { distributed: MentorRow[]; total_distributed: string };
}

function fmtNum(v: string | number | null | undefined, digits = 4): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('zh-CN', { maximumFractionDigits: digits });
}

function fmtPct(ratio: string): string {
  const n = Number(ratio);
  if (Number.isNaN(n)) return ratio;
  return `${(n * 100).toFixed(2)}%`;
}

function PreviewPanel({ data }: { data: PreviewResult }) {
  const pairingAmt = Number(data.pairing.pairing_amount);
  const award = Number(data.gross_balance_award);

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="本次对碰量" value={fmtNum(data.pairing.pairing_amount)} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="平衡奖（毛额 EP）"
              value={fmtNum(data.gross_balance_award)}
              suffix={`× ${fmtPct(data.balance_award_pct)}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="对碰后左/右结转"
              value={`${fmtNum(data.pairing.leg_l_after)} / ${fmtNum(data.pairing.leg_r_after)}`}
            />
          </Card>
        </Col>
      </Row>

      <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small" title="结转与对碰（不落账）">
        <Descriptions.Item label="用户 ID">{data.state.user_id}</Descriptions.Item>
        <Descriptions.Item label="平衡奖比例">{fmtPct(data.balance_award_pct)}</Descriptions.Item>
        <Descriptions.Item label="对碰前 · 左区结转">{fmtNum(data.state.leg_l)}</Descriptions.Item>
        <Descriptions.Item label="对碰前 · 右区结转">{fmtNum(data.state.leg_r)}</Descriptions.Item>
        <Descriptions.Item label="本次对碰量">{fmtNum(data.pairing.pairing_amount)}</Descriptions.Item>
        <Descriptions.Item label="预估平衡奖 EP">{fmtNum(data.gross_balance_award)}</Descriptions.Item>
        <Descriptions.Item label="对碰后 · 左区结转">{fmtNum(data.pairing.leg_l_after)}</Descriptions.Item>
        <Descriptions.Item label="对碰后 · 右区结转">{fmtNum(data.pairing.leg_r_after)}</Descriptions.Item>
      </Descriptions>

      {pairingAmt <= 0 && (
        <Alert
          style={{ marginTop: 12 }}
          type="info"
          showIcon
          message="本次无对碰量，不会产生平衡奖。"
        />
      )}
      {pairingAmt > 0 && award > 0 && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
          公式：平衡奖 EP ≈ 对碰量 × 平衡奖比例。子账号不发放平衡奖；实际入账受 EP 周封顶约束。
        </Typography.Paragraph>
      )}
    </>
  );
}

function ExecutePanel({ data }: { data: ExecuteResult }) {
  const ba = data.balance_award;
  const skippedSub = ba.skipped === 'sub_account';

  return (
    <>
      <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small" title="对碰结转（已写入）" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="用户 ID">{data.pairing.user_id}</Descriptions.Item>
        <Descriptions.Item label="本次对碰量">{fmtNum(data.pairing.pairing_amount)}</Descriptions.Item>
        <Descriptions.Item label="左区新增业绩">{fmtNum(data.pairing.left_new)}</Descriptions.Item>
        <Descriptions.Item label="右区新增业绩">{fmtNum(data.pairing.right_new)}</Descriptions.Item>
        <Descriptions.Item label="对碰前 · 左">{fmtNum(data.pairing.leg_l_before)}</Descriptions.Item>
        <Descriptions.Item label="对碰前 · 右">{fmtNum(data.pairing.leg_r_before)}</Descriptions.Item>
        <Descriptions.Item label="对碰后 · 左">{fmtNum(data.pairing.leg_l_after)}</Descriptions.Item>
        <Descriptions.Item label="对碰后 · 右">{fmtNum(data.pairing.leg_r_after)}</Descriptions.Item>
      </Descriptions>

      <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small" title="平衡奖入账" style={{ marginBottom: 16 }}>
        {ba.requested != null && (
          <Descriptions.Item label="申请金额 EP">{fmtNum(ba.requested)}</Descriptions.Item>
        )}
        <Descriptions.Item label="实际入账 EP">
          <Typography.Text strong>{fmtNum(ba.awarded)}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="是否触达 EP 周封顶">
          {ba.capped ? '是（已按剩余额度截断）' : '否'}
        </Descriptions.Item>
        {ba.remaining_after != null && (
          <Descriptions.Item label="封顶后剩余额度">{fmtNum(ba.remaining_after)}</Descriptions.Item>
        )}
        {skippedSub && (
          <Descriptions.Item label="跳过原因" span={2}>
            子账号不发放平衡奖（§4）
          </Descriptions.Item>
        )}
      </Descriptions>

      <Typography.Title level={5} style={{ marginTop: 8 }}>
        辅导奖分发
      </Typography.Title>
      {data.mentorship.distributed.length === 0 ? (
        <Typography.Text type="secondary">无辅导奖（无对碰/子号/无上级或金额为 0）</Typography.Text>
      ) : (
        <Table<MentorRow>
          size="small"
          bordered
          pagination={false}
          rowKey={(r) => `${r.mentor_id}-${r.circle}`}
          dataSource={data.mentorship.distributed}
          columns={[
            { title: '辅导人 ID', dataIndex: 'mentor_id', width: 90 },
            { title: '等级', dataIndex: 'mentor_level', width: 72 },
            { title: '圈数', dataIndex: 'circle', width: 56 },
            {
              title: '比例',
              dataIndex: 'pct',
              width: 72,
              render: (v: string) => fmtPct(v),
            },
            { title: '基数 EP', dataIndex: 'base_amount', render: (v: string) => fmtNum(v) },
            { title: '辅导奖 EP', dataIndex: 'bonus_amount', render: (v: string) => fmtNum(v) },
            { title: '来源会员', dataIndex: 'source_user_id', width: 90 },
          ]}
          footer={() => (
            <Typography.Text>
              辅导奖合计：<strong>{fmtNum(data.mentorship.total_distributed)}</strong> EP
            </Typography.Text>
          )}
        />
      )}
    </>
  );
}

export function DynamicRewardOpsPage() {
  const [userId, setUserId] = useState('');
  const [leftNew, setLeftNew] = useState('');
  const [rightNew, setRightNew] = useState('');
  const [carryover, setCarryover] = useState<PairingState | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [result, setResult] = useState<ExecuteResult | null>(null);
  const [loading, setLoading] = useState(false);

  async function loadCarryover() {
    const uid = Number(userId);
    if (!uid) return;
    try {
      const res = await api.get<PairingState>(`/api/admin/dynamic-rewards/pairing/${uid}`);
      setCarryover(res);
    } catch {
      setCarryover(null);
    }
  }

  async function runPreview() {
    await callApi('/api/admin/dynamic-rewards/balance-event/preview', (v) => {
      setPreview(v as unknown as PreviewResult);
      setResult(null);
    });
    await loadCarryover();
  }

  async function runExecute() {
    await callApi('/api/admin/dynamic-rewards/balance-event', (v) => {
      setResult(v as unknown as ExecuteResult);
      setPreview(null);
    });
    await loadCarryover();
  }

  async function callApi(path: string, setter: (v: Record<string, unknown>) => void) {
    const uid = Number(userId);
    if (!uid) {
      message.warning('请输入用户 ID');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post<Record<string, unknown>>(path, {
        user_id: uid,
        left_new_perf: leftNew || '0',
        right_new_perf: rightNew || '0',
      });
      setter(res);
      message.success(path.includes('preview') ? '预览完成' : '已写账');
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '请求失败');
      if (path.includes('preview')) setPreview(null);
      else setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Typography.Title level={4}>动态奖励 · 对碰 / 平衡奖</Typography.Title>
      <Typography.Paragraph type="secondary">
        模拟或写入一次「左/右区新增业绩 → 双轨对碰 → 平衡奖 EP → 辅导奖」链路。预览不落账；执行会更新结转并写账本。
        平衡奖比例可在 <strong>系统管理 → 奖励分发配置</strong> 中修改。
      </Typography.Paragraph>

      <Card size="small" style={{ maxWidth: 520, marginBottom: 16 }}>
        <Form layout="vertical" size="middle">
          <Form.Item label="会员用户 ID" required>
            <Input
              placeholder="例如 10360"
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                setCarryover(null);
              }}
              onBlur={() => loadCarryover()}
            />
          </Form.Item>
          <Form.Item label="左区新增业绩">
            <Input
              placeholder="本次投喂到左区的业绩量"
              value={leftNew}
              onChange={(e) => setLeftNew(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="右区新增业绩">
            <Input
              placeholder="本次投喂到右区的业绩量"
              value={rightNew}
              onChange={(e) => setRightNew(e.target.value)}
            />
          </Form.Item>
          {carryover && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                <span>
                  当前结转：左区 <strong>{fmtNum(carryover.leg_l)}</strong>，右区{' '}
                  <strong>{fmtNum(carryover.leg_r)}</strong>
                </span>
              }
            />
          )}
          <Space>
            <Button onClick={runPreview} loading={loading}>
              预览（不写账）
            </Button>
            <Button type="primary" onClick={runExecute} loading={loading}>
              执行并写账
            </Button>
            <Button type="link" onClick={loadCarryover} disabled={!userId}>
              刷新结转
            </Button>
          </Space>
        </Form>
      </Card>

      {preview && (
        <Card title="预览结果" size="small" style={{ marginTop: 8 }}>
          <PreviewPanel data={preview} />
        </Card>
      )}

      {result && (
        <Card title="执行结果（已落账）" size="small" style={{ marginTop: 8 }}>
          <ExecutePanel data={result} />
        </Card>
      )}

      {!preview && !result && !loading && (
        <Typography.Text type="secondary">
          填写用户 ID 与左右新增业绩后点击「预览」或「执行并写账」。
        </Typography.Text>
      )}
    </div>
  );
}
